/**
 * Calculadora Planilha — import Excel (produto+cor, vendas 90d, Est.Segur.)
 * e ajuste de dias até a meta (mesma lógica da Calculadora Automática).
 */

import * as XLSX from 'xlsx';
import {
  interpretarMatrizBoletim,
  parseNumeroPlanilhaBoletim,
} from './leituraBoletimParse';
import {
  parseProdutoCelulaExcel,
  resolverNomeProdutoPontuacao,
  normalizarTextoProdutoImportacao,
} from './resolverProdutoPontuacao';

export interface LinhaCalcPlanilha {
  id: string;
  selecionada: boolean;
  produtoNome: string;
  corProduto: string;
  codigoProduto: string;
  textoOriginal: string;
  vendas90: number;
  estSegur: number;
  /** Quantidade já definida nesta remessa (não entra no igualar dias). */
  qtdJaEntra: number;
  encontradoCatalogo: boolean;
}

/** Nome + cor + código(s) como no Excel — para resumo fora do sistema. */
export function rotuloCompletoLinhaCalc(
  l: Pick<LinhaCalcPlanilha, 'textoOriginal' | 'produtoNome' | 'corProduto' | 'codigoProduto'>
): string {
  const orig = String(l.textoOriginal || '').trim();
  if (orig) return orig;
  return [l.produtoNome, l.corProduto, l.codigoProduto].filter((x) => String(x || '').trim()).join(' ');
}

export interface ResultadoLinhaCalcPlanilha {
  id: string;
  diaAjustado: number | null;
  quantidade: number;
  pontos: number;
  modo: 'fixa' | 'ajustada' | 'fora';
}

export interface ResultadoCalcPlanilha {
  porLinha: Record<string, ResultadoLinhaCalcPlanilha>;
  pontosFixos: number;
  pontosAjustados: number;
  total: number;
  meta: number;
  metaRestante: number;
  diaAlvo?: number | null;
}

/** Grupo fechado na Planilha (dias igualados só entre os membros). */
export interface GrupoCalcPlanilha {
  id: string;
  idsLinhas: string[];
  rotulo: string;
  /** Meta da caixa no momento em que o grupo foi fechado. */
  metaDoGrupo: number;
}

export interface GrupoCalcPlanilhaCalculado extends GrupoCalcPlanilha {
  ordem: number;
  metaDoGrupo: number;
  diaAlvo: number | null;
  resultado: ResultadoCalcPlanilha | null;
}

export interface ResultadoParseCalcPlanilha {
  linhas: LinhaCalcPlanilha[];
  avisos: string[];
  nomeArquivo: string;
}

function normalizarCabecalho(s: string): string {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim();
}

function celulaTexto(val: unknown): string {
  if (val == null || val === '') return '';
  return String(val).trim();
}

function classificarColuna(header: string): 'produto' | 'vendas' | 'dias' | null {
  const t = normalizarCabecalho(header);
  if (!t) return null;
  if (t.includes('PRODUTO') || t.includes('DESCRIC') || t === 'ITEM' || t === 'NOME') return 'produto';
  if (t.includes('PROC') && t.includes('EST')) return null;
  if (/\bEST\.?\s*SEGUR/.test(t) || t.includes('ESTSEG') || t.includes('EST.SEG') || t.includes('SEGUR'))
    return 'dias';
  if (t.includes('VENDAS') || t.includes('VENDA') || /\b90\b/.test(t) || t.includes('90D')) return 'vendas';
  if ((t.includes('DIA') || t.includes('COBERT')) && !t.includes('90') && !t.includes('VEND')) return 'dias';
  return null;
}

function ehCabecalhoEstSegur(header: string): boolean {
  const n = normalizarCabecalho(header).replace(/\s/g, '');
  if (!n) return false;
  if (n.includes('PROC')) return false;
  return /EST\.?SEGUR/.test(n) || n.includes('ESTSEGUR') || n === 'SEGUR' || n.endsWith('SEGUR') || n.includes('SEGUR');
}

function textoTemSinalNegativo(s: string): boolean {
  const t = String(s || '')
    .replace(/[\s\u00A0\u202F]/g, '')
    .replace(/[\u2212\u2013\u2014\uFF0D\uFE63]/g, '-');
  if (!t) return false;
  if (t.startsWith('(') && t.endsWith(')')) return true;
  if (t.startsWith('-')) return true;
  if (/^\d+[.,]?\d*-$/.test(t)) return true;
  return false;
}

/** Dia atual / Est.Segur. — o sinal vale (atraso = negativo). */
export function parseDiaEstSegur(val: unknown): number | null {
  if (val == null || val === '') return null;
  if (typeof val === 'number' && Number.isFinite(val)) return val;
  if (typeof val === 'boolean') return null;
  if (val instanceof Date) return null;
  const original = String(val);
  let s = original
    .trim()
    .replace(/^=+/, '')
    .replace(/[\s\u00A0\u202F]/g, '')
    .replace(/[\u2212\u2013\u2014\uFF0D\uFE63]/g, '-');
  if (!s || s === '-' || s === '—') return null;

  const paren = s.startsWith('(') && s.endsWith(')');
  if (paren) s = s.slice(1, -1);
  let trailingNeg = false;
  if (s.length > 1 && s.endsWith('-') && !s.startsWith('-')) {
    trailingNeg = true;
    s = s.slice(0, -1);
  }

  const direto = parseNumeroPlanilhaBoletim(s);
  if (direto != null) {
    if (paren || trailingNeg) return direto === 0 ? 0 : -Math.abs(direto);
    return direto;
  }

  const m = s.match(/-?\d+(?:[.,]\d+)?/);
  if (!m) return null;
  const n = parseNumeroPlanilhaBoletim(m[0]);
  if (n == null) return null;
  if (paren || trailingNeg || textoTemSinalNegativo(original)) return n === 0 ? 0 : -Math.abs(n);
  return n;
}

function numeroDiaCelula(bruto: unknown, formatado: unknown): number | null {
  const f = parseDiaEstSegur(formatado);
  const b = parseDiaEstSegur(bruto);
  if (f != null && f < 0) return f;
  if (b != null && b < 0) return b;
  if (textoTemSinalNegativo(String(formatado ?? '')) || textoTemSinalNegativo(String(bruto ?? ''))) {
    const mag = f != null ? Math.abs(f) : b != null ? Math.abs(b) : null;
    if (mag != null) return mag === 0 ? 0 : -mag;
  }
  if (b != null) return b;
  return f;
}

function diaDeCelulaExcel(cell: XLSX.CellObject | undefined): number | null {
  if (!cell) return null;
  let formatado = '';
  try {
    formatado = String(XLSX.utils.format_cell(cell) ?? '');
  } catch {
    formatado = cell.w != null ? String(cell.w) : '';
  }
  if (!formatado && cell.w != null) formatado = String(cell.w);
  const html = cell.h != null ? String(cell.h).replace(/<[^>]*>/g, ' ') : '';
  const candidatos = [cell.v, formatado, cell.w, html];
  let melhor: number | null = null;
  for (const c of candidatos) {
    const n = parseDiaEstSegur(c);
    if (n == null) continue;
    if (n < 0) return n;
    if (melhor == null) melhor = n;
  }
  const textos = [formatado, String(cell.w ?? ''), html, String(cell.v ?? '')];
  if (textos.some((t) => textoTemSinalNegativo(t))) {
    if (melhor != null && melhor !== 0) return -Math.abs(melhor);
  }
  return melhor;
}

function contarValoresDiaNaColuna(
  matrizBruta: unknown[][],
  matrizFmt: unknown[][],
  col: number,
  inicio: number
): { finitos: number; naoZero: number; negativos: number } {
  let finitos = 0;
  let naoZero = 0;
  let negativos = 0;
  for (let r = inicio; r < matrizBruta.length; r++) {
    const n = numeroDiaCelula(matrizBruta[r]?.[col], matrizFmt[r]?.[col]);
    if (n == null) continue;
    finitos += 1;
    if (n !== 0) naoZero += 1;
    if (n < 0) negativos += 1;
  }
  return { finitos, naoZero, negativos };
}

function escolherColunaEstSegur(
  cabecalho: string[],
  matrizBruta: unknown[][],
  matrizFmt: unknown[][],
  inicioDados: number
): number {
  const candidatos: number[] = [];
  cabecalho.forEach((h, i) => {
    if (ehCabecalhoEstSegur(h) && !candidatos.includes(i)) candidatos.push(i);
  });
  if (candidatos.length === 0) {
    cabecalho.forEach((h, i) => {
      if (classificarColuna(h) === 'dias' && !candidatos.includes(i)) candidatos.push(i);
    });
  }
  if (candidatos.length === 0) return -1;

  let melhor = candidatos[0];
  let melhorPts = -1;
  for (const c of candidatos) {
    const { finitos, naoZero, negativos } = contarValoresDiaNaColuna(
      matrizBruta,
      matrizFmt,
      c,
      inicioDados
    );
    const cabOk = ehCabecalhoEstSegur(cabecalho[c] || '');
    const pts =
      (cabOk ? 10000 : 0) + negativos * 100 + naoZero * 10 + finitos;
    if (pts > melhorPts) {
      melhorPts = pts;
      melhor = c;
    }
  }
  return melhor;
}

function montarCabecalho(
  matriz: unknown[][],
  idx: number
): { cabecalho: string[]; primeiraDados: number } {
  const linha = (r: number) =>
    (matriz[r] || []).map((c) => celulaTexto(c).replace(/[\r\n]+/g, ' '));
  const a = linha(idx);
  const b = linha(idx + 1);
  const aN = a.map(normalizarCabecalho).join(' ');
  const bN = b.map(normalizarCabecalho).join(' ');
  const subCab =
    idx + 1 < matriz.length &&
    !/PRODUTO|DESCRIC/.test(bN) &&
    (/SEGUR/.test(bN) || /\b90\b/.test(bN) || (/DIA/.test(bN) && !/SEGUR/.test(aN)));
  if (subCab) {
    const len = Math.max(a.length, b.length);
    const cab = Array.from({ length: len }, (_, i) => `${a[i] || ''} ${b[i] || ''}`.trim());
    return { cabecalho: cab, primeiraDados: idx + 2 };
  }
  return { cabecalho: a, primeiraDados: idx + 1 };
}

function detectarCabecalho(matriz: unknown[][]): number {
  let melhor = 0;
  let ptsMelhor = -1;
  const limite = Math.min(15, matriz.length);
  for (let i = 0; i < limite; i++) {
    const row = matriz[i] || [];
    const tipos = new Set(
      row.map((c) => classificarColuna(celulaTexto(c))).filter(Boolean)
    );
    const pts = tipos.size;
    if (pts > ptsMelhor) {
      ptsMelhor = pts;
      melhor = i;
    }
  }
  return ptsMelhor >= 2 ? melhor : 0;
}

function arredondarParaCinco(n: number): number {
  return Math.round(Math.max(0, n) / 5) * 5;
}

function totalPontosQtd(
  produtos: ReadonlyArray<{ m2: number }>,
  quantidades: readonly number[]
): number {
  return produtos.reduce((s, p, i) => s + p.m2 * (quantidades[i] ?? 0), 0);
}

function diaEfetivo(diaInicial: number, qtd: number, porDia: number): number {
  if (!(porDia > 0)) return diaInicial;
  return diaInicial + qtd / porDia;
}

function qtdPisoParaDia(
  p: { diaInicial: number; porDia: number },
  diaMin: number
): number {
  const n = Math.max(0, (diaMin - p.diaInicial) * p.porDia);
  return Math.floor((n + 1e-9) / 5) * 5;
}

/**
 * Passa 5 peças de quem tem muita qtd/venda (o dia quase não cai) para quem
 * ficou com poucos dias (ex.: 6,3 → sobe; 300 → 295).
 */
function nivelarDiasComCinco(
  produtos: ReadonlyArray<{ m2: number; porDia: number; diaInicial: number }>,
  qs: number[],
  meta: number
): void {
  if (produtos.length < 2) return;
  for (let n = 0; n < 4000; n++) {
    const dias = produtos.map((p, i) => diaEfetivo(p.diaInicial, qs[i] ?? 0, p.porDia));
    let iMin = 0;
    for (let i = 1; i < dias.length; i++) {
      if (dias[i] < dias[iMin]) iMin = i;
    }
    let donor = -1;
    let donorScore = -1;
    for (let i = 0; i < produtos.length; i++) {
      if (i === iMin || (qs[i] ?? 0) < 5) continue;
      if (dias[i] < dias[iMin] + 0.45) continue;
      const score = produtos[i].porDia * 1000 + (qs[i] ?? 0);
      if (score > donorScore) {
        donorScore = score;
        donor = i;
      }
    }
    if (donor < 0) break;
    const trial = qs.slice();
    trial[donor] -= 5;
    trial[iMin] += 5;
    const t = totalPontosQtd(produtos, trial);
    if (t + 1e-9 < meta) break;
    const dias2 = produtos.map((p, i) => diaEfetivo(p.diaInicial, trial[i] ?? 0, p.porDia));
    const spreadOld = Math.max(...dias) - Math.min(...dias);
    const spreadNew = Math.max(...dias2) - Math.min(...dias2);
    if (spreadNew >= spreadOld - 1e-9) break;
    qs[donor] = trial[donor] ?? 0;
    qs[iMin] = trial[iMin] ?? 0;
  }
}

/**
 * Chega o mais perto da meta por cima. Só tira 5 de quem o dia quase não desce
 * (venda/dia alta e qtd grande). Depois nivela os atrasados.
 */
function apararQuantidadesAcimaDaMeta(
  produtos: ReadonlyArray<{ m2: number; porDia: number; diaInicial: number; alvo?: number }>,
  quantidades: readonly number[],
  meta: number,
  alvoPadrao?: number
): { quantidades: number[]; total: number } {
  const qs = quantidades.map((q) => arredondarParaCinco(q));
  let tot = totalPontosQtd(produtos, qs);
  if (produtos.length === 0) return { quantidades: qs, total: tot };

  const alvoDe = (i: number) => {
    const a = produtos[i]?.alvo;
    if (a != null && Number.isFinite(a)) return a;
    if (alvoPadrao != null && Number.isFinite(alvoPadrao)) return alvoPadrao;
    return diaEfetivo(produtos[i].diaInicial, qs[i] ?? 0, produtos[i].porDia);
  };

  if (meta > 0 && tot > meta) {
    for (let folga = 0.35; folga <= 10 && tot > meta + 1e-9; folga += 0.35) {
      const ordem = produtos
        .map((p, i) => ({ i, porDia: p.porDia, qtd: qs[i] ?? 0 }))
        .sort((a, b) => b.porDia - a.porDia || b.qtd - a.qtd);
      let mudou = false;
      for (const { i } of ordem) {
        if ((qs[i] ?? 0) < 5) continue;
        const piso = qtdPisoParaDia(produtos[i], alvoDe(i) - folga);
        if ((qs[i] ?? 0) - 5 < piso) continue;
        const trial = qs.slice();
        trial[i] -= 5;
        const t = totalPontosQtd(produtos, trial);
        if (t + 1e-9 >= meta) {
          qs[i] = trial[i] ?? 0;
          tot = t;
          mudou = true;
          if (tot <= meta + 1e-9) break;
        }
      }
      if (!mudou) continue;
    }
  }

  tot = totalPontosQtd(produtos, qs);
  return { quantidades: qs, total: tot };
}

function m2Produto(
  nome: string,
  pontuacoes: ReadonlyArray<{ nome: string; m2: number }>,
  textoOriginal?: string
): number {
  const alvo = String(nome || '').trim();
  if (!alvo && !textoOriginal) return 0;
  const nomes = pontuacoes.map((p) => p.nome);
  for (const fonte of [alvo, textoOriginal].filter((x) => String(x || '').trim())) {
    const resolvido = resolverNomeProdutoPontuacao(String(fonte), nomes, textoOriginal || alvo);
    const p =
      pontuacoes.find((x) => x.nome === resolvido) ||
      pontuacoes.find(
        (x) =>
          normalizarTextoProdutoImportacao(x.nome) === normalizarTextoProdutoImportacao(resolvido)
      );
    if (p && Number(p.m2) > 0) return Number(p.m2);
  }
  return 0;
}

function calcQsNoAlvo(
  produtos: ReadonlyArray<{ m2: number; porDia: number; diaInicial: number }>,
  alvo: number
): { qs: number[]; tot: number; alvo: number } {
  const qs = produtos.map((p) =>
    arredondarParaCinco(Math.max(0, alvo - p.diaInicial) * p.porDia)
  );
  return { qs, tot: totalPontosQtd(produtos, qs), alvo };
}

/**
 * Alvo de dias sobe a partir de quem está mais atrasado, até a meta.
 * Não persegue quem já tem 200–475 dias — esses ficam qtd 0.
 */
function encontrarAlvoDiasParaMeta(
  produtos: ReadonlyArray<{ m2: number; porDia: number; diaInicial: number }>,
  meta: number
): { qs: number[]; tot: number; alvo: number } {
  if (produtos.length === 0 || !(meta > 0)) {
    const minDia =
      produtos.length > 0 ? Math.min(...produtos.map((p) => p.diaInicial)) : 0;
    return { qs: produtos.map(() => 0), tot: 0, alvo: minDia };
  }
  const minDia = Math.min(...produtos.map((p) => p.diaInicial));
  let alvo = Math.floor(minDia);
  let atual = calcQsNoAlvo(produtos, alvo);
  const teto = minDia + 8000;
  while (atual.tot < meta && alvo < teto) {
    alvo += 1;
    atual = calcQsNoAlvo(produtos, alvo);
  }
  while (alvo > Math.floor(minDia)) {
    const ant = calcQsNoAlvo(produtos, alvo - 1);
    if (ant.tot < meta) break;
    alvo -= 1;
    atual = ant;
  }
  return atual;
}

/**
 * Iguala os que precisam produzir no mesmo dia-alvo (sobe a partir do mais atrasado).
 * Quem já está acima do alvo não entra na produção (qtd 0).
 */
export function ajustarDiasAteMeta(
  produtos: { m2: number; porDia: number; diaInicial: number; origIdx: number }[],
  meta: number
): { dias: number[]; quantidades: number[]; total: number; origIdxs: number[]; alvo: number } {
  if (produtos.length === 0 || meta <= 0) {
    return { dias: [], quantidades: [], total: 0, origIdxs: [], alvo: 0 };
  }
  const origIdxs = produtos.map((p) => p.origIdx);
  const atual = encontrarAlvoDiasParaMeta(produtos, meta);

  const aparado = apararQuantidadesAcimaDaMeta(produtos, atual.qs, meta, atual.alvo);
  const qsFinais = aparado.quantidades.slice();
  nivelarDiasComCinco(produtos, qsFinais, meta);
  const totalFinal = totalPontosQtd(produtos, qsFinais);
  const dias = produtos.map((p, i) => diaEfetivo(p.diaInicial, qsFinais[i] ?? 0, p.porDia));

  return {
    dias,
    quantidades: qsFinais,
    total: totalFinal,
    origIdxs,
    alvo: atual.alvo,
  };
}

export function calcularPlanilhaMeta(
  linhas: LinhaCalcPlanilha[],
  pontuacoes: ReadonlyArray<{ nome: string; m2: number }>,
  metaBruta: number
): ResultadoCalcPlanilha | null {
  const meta = Number(metaBruta);
  if (!Number.isFinite(meta) || meta <= 0) return null;

  const porLinha: Record<string, ResultadoLinhaCalcPlanilha> = {};
  let pontosFixos = 0;
  const paraAjustar: { m2: number; porDia: number; diaInicial: number; origIdx: number; id: string }[] =
    [];

  linhas.forEach((l, origIdx) => {
    const m2 = m2Produto(l.produtoNome, pontuacoes, l.textoOriginal);
    if (!l.selecionada) {
      porLinha[l.id] = {
        id: l.id,
        diaAjustado: null,
        quantidade: 0,
        pontos: 0,
        modo: 'fora',
      };
      return;
    }
    const qtdFixa = Number(l.qtdJaEntra) || 0;
    if (qtdFixa > 0) {
      const pts = m2 * qtdFixa;
      pontosFixos += pts;
      porLinha[l.id] = {
        id: l.id,
        diaAjustado: l.estSegur,
        quantidade: qtdFixa,
        pontos: pts,
        modo: 'fixa',
      };
      return;
    }
    const porDia = (Number(l.vendas90) || 0) / 90;
    if (m2 <= 0 || porDia <= 0) {
      porLinha[l.id] = {
        id: l.id,
        diaAjustado: l.estSegur,
        quantidade: 0,
        pontos: 0,
        modo: 'ajustada',
      };
      return;
    }
    paraAjustar.push({
      m2,
      porDia,
      diaInicial: Number.isFinite(l.estSegur) ? l.estSegur : 0,
      origIdx,
      id: l.id,
    });
  });

  const metaRestante = Math.max(0, meta - pontosFixos);
  const ajuste = ajustarDiasAteMeta(
    paraAjustar.map(({ m2, porDia, diaInicial, origIdx }) => ({
      m2,
      porDia,
      diaInicial,
      origIdx,
    })),
    metaRestante
  );
  paraAjustar.forEach((p, i) => {
    const qtd = ajuste.quantidades[i] ?? 0;
    const dia = ajuste.dias[i] ?? p.diaInicial;
    porLinha[p.id] = {
      id: p.id,
      diaAjustado: dia,
      quantidade: qtd,
      pontos: p.m2 * qtd,
      modo: 'ajustada',
    };
  });

  const pontosAjustados = Object.values(porLinha)
    .filter((r) => r.modo === 'ajustada')
    .reduce((s, r) => s + r.pontos, 0);
  const total = pontosFixos + pontosAjustados;

  return {
    porLinha,
    pontosFixos,
    pontosAjustados,
    total,
    meta,
    metaRestante,
    diaAlvo: ajuste.alvo ?? null,
  };
}

export function rotuloGrupoDasLinhas(
  linhas: LinhaCalcPlanilha[],
  ids: readonly string[]
): string {
  const set = new Set(ids);
  const nomes: string[] = [];
  for (const l of linhas) {
    if (!set.has(l.id)) continue;
    const n = String(l.produtoNome || rotuloCompletoLinhaCalc(l)).trim();
    const curto = n.split(/\s+/).slice(0, 4).join(' ');
    if (curto && !nomes.includes(curto)) nomes.push(curto);
  }
  if (nomes.length === 0) return 'Grupo';
  if (nomes.length <= 3) return nomes.join(' + ');
  return `${nomes.slice(0, 2).join(' + ')} + ${nomes.length - 2} mais`;
}

export function recalcularGruposPlanilha(
  linhas: LinhaCalcPlanilha[],
  grupos: GrupoCalcPlanilha[],
  pontuacoes: ReadonlyArray<{ nome: string; m2: number }>,
  metaCaixa: number
): GrupoCalcPlanilhaCalculado[] {
  const fallback = Number(metaCaixa);
  const fallbackOk = Number.isFinite(fallback) && fallback > 0 ? fallback : 0;

  return grupos.map((g, idx) => {
    const gravada = Number(g.metaDoGrupo);
    const metaG = Number.isFinite(gravada) && gravada > 0 ? gravada : fallbackOk;
    const ids = new Set(g.idsLinhas);
    const subset = linhas.map((l) => ({ ...l, selecionada: ids.has(l.id) }));
    const resultado = metaG > 0 ? calcularPlanilhaMeta(subset, pontuacoes, metaG) : null;
    return {
      ...g,
      rotulo: g.rotulo || rotuloGrupoDasLinhas(linhas, g.idsLinhas),
      ordem: idx + 1,
      metaDoGrupo: metaG,
      diaAlvo: resultado?.diaAlvo ?? null,
      resultado,
    };
  });
}

export function mesclarPorLinhaGrupos(
  grupos: GrupoCalcPlanilhaCalculado[],
  previa: ResultadoCalcPlanilha | null
): Record<string, ResultadoLinhaCalcPlanilha> {
  const porLinha: Record<string, ResultadoLinhaCalcPlanilha> = {};
  for (const g of grupos) {
    if (!g.resultado) continue;
    for (const [id, r] of Object.entries(g.resultado.porLinha)) {
      if (r.modo === 'fora') continue;
      porLinha[id] = r;
    }
  }
  if (previa) {
    for (const [id, r] of Object.entries(previa.porLinha)) {
      if (r.modo === 'fora') continue;
      porLinha[id] = r;
    }
  }
  return porLinha;
}

export function parseArquivoCalcPlanilha(
  data: ArrayBuffer,
  nomeArquivo: string,
  nomesPontuacao: readonly string[]
): ResultadoParseCalcPlanilha {
  const avisos: string[] = [];
  const wb = XLSX.read(data, {
    type: 'array',
    cellDates: false,
    cellNF: true,
    cellText: true,
  });
  const nomeAba = wb.SheetNames[0];
  if (!nomeAba) {
    return { linhas: [], avisos: ['Planilha sem abas.'], nomeArquivo };
  }
  const ws = wb.Sheets[nomeAba];
  const raw = XLSX.utils.sheet_to_json<(string | number | null)[]>(ws, {
    header: 1,
    defval: '',
    raw: true,
  });
  const fmt = XLSX.utils.sheet_to_json<(string | number | null)[]>(ws, {
    header: 1,
    defval: '',
    raw: false,
  });
  const temConteudo = (row: unknown) =>
    (Array.isArray(row) ? row : []).some((c) => celulaTexto(c) !== '');
  const nLinhas = Math.max(raw.length, fmt.length);
  const matriz: unknown[][] = [];
  const matrizFmt: unknown[][] = [];
  const origRows: number[] = [];
  for (let i = 0; i < nLinhas; i++) {
    const r = raw[i];
    const f = fmt[i];
    if (!temConteudo(r) && !temConteudo(f)) continue;
    matriz.push(Array.isArray(r) ? r : []);
    matrizFmt.push(Array.isArray(f) ? f : []);
    origRows.push(i);
  }
  if (matriz.length < 2) {
    return { linhas: [], avisos: ['Não achei linhas de produto na planilha.'], nomeArquivo };
  }

  const idxHeader = detectarCabecalho(matriz);
  const { cabecalho, primeiraDados } = montarCabecalho(matriz, idxHeader);
  let colProduto = -1;
  let colVendas = -1;
  cabecalho.forEach((h, i) => {
    const t = classificarColuna(h);
    if (t === 'produto' && colProduto < 0) colProduto = i;
    if (t === 'vendas' && colVendas < 0) colVendas = i;
  });
  const colDias = escolherColunaEstSegur(cabecalho, matriz, matrizFmt, primeiraDados);
  if (colProduto < 0) colProduto = 0;
  if (colVendas < 0) avisos.push('Coluna de vendas (90 dias) não encontrada — conferir cabeçalho.');
  if (colDias < 0) avisos.push('Coluna Est.Segur. (dia atual) não encontrada — conferir cabeçalho.');

  const matrizTxt: string[][] = matrizFmt.map((row) =>
    (Array.isArray(row) ? row : []).map((c) => celulaTexto(c))
  );
  const interp = interpretarMatrizBoletim(matrizTxt);
  const filaBoletimPorProduto = new Map<string, number[]>();
  for (const bl of interp.linhas) {
    const k = String(bl.produto || '').trim();
    if (!k) continue;
    const dia =
      bl.estSegurPlanilha != null && Number.isFinite(bl.estSegurPlanilha)
        ? bl.estSegurPlanilha
        : bl.estSegurCalc != null && Number.isFinite(bl.estSegurCalc)
          ? bl.estSegurCalc
          : null;
    if (dia == null) continue;
    const arr = filaBoletimPorProduto.get(k) ?? [];
    arr.push(dia);
    filaBoletimPorProduto.set(k, arr);
  }
  const cursorBoletim = new Map<string, number>();
  const diaFallbackBoletim = (produto: string): number | null => {
    const k = produto.trim();
    const arr = filaBoletimPorProduto.get(k);
    if (!arr || arr.length === 0) return null;
    const i = cursorBoletim.get(k) ?? 0;
    cursorBoletim.set(k, i + 1);
    return arr[Math.min(i, arr.length - 1)] ?? null;
  };

  const linhas: LinhaCalcPlanilha[] = [];
  for (let r = primeiraDados; r < matriz.length; r++) {
    const row = matriz[r] || [];
    const rowFmt = matrizFmt[r] || [];
    const textoProd = celulaTexto(rowFmt[colProduto] ?? row[colProduto]);
    if (!textoProd) continue;
    if (/segur/i.test(textoProd) && textoProd.length < 24) continue;
    const parsed = parseProdutoCelulaExcel(textoProd, nomesPontuacao);
    const vendas90 =
      colVendas >= 0
        ? numeroDiaCelula(row[colVendas], rowFmt[colVendas]) ?? 0
        : 0;
    const cellDias =
      colDias >= 0 && origRows[r] != null
        ? ws[XLSX.utils.encode_cell({ r: origRows[r], c: colDias })]
        : undefined;
    let estSegur = colDias >= 0 ? diaDeCelulaExcel(cellDias) : null;
    if (estSegur == null && colDias >= 0) {
      estSegur = numeroDiaCelula(row[colDias], rowFmt[colDias]);
    }
    if (estSegur == null) {
      estSegur = diaFallbackBoletim(textoProd);
    }
    linhas.push({
      id: `cp-${r}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`,
      selecionada: false,
      produtoNome: parsed.produtoNome,
      corProduto: parsed.corProduto || '',
      codigoProduto: parsed.codigoProduto || '',
      textoOriginal: parsed.textoProdutoOriginal,
      vendas90,
      estSegur: estSegur ?? 0,
      qtdJaEntra: 0,
      encontradoCatalogo:
        parsed.encontradoCatalogo ||
        m2Produto(parsed.produtoNome, nomesPontuacao.map((nome) => ({ nome, m2: 1 })), parsed.textoProdutoOriginal) >
          0,
    });
  }

  if (linhas.length > 0 && linhas.every((l) => l.estSegur === 0)) {
    cursorBoletim.clear();
    for (const l of linhas) {
      const d = diaFallbackBoletim(l.textoOriginal);
      if (d != null) l.estSegur = d;
    }
  }

  if (linhas.length === 0) avisos.push('Nenhum produto lido nas linhas abaixo do cabeçalho.');
  if (linhas.length > 0 && linhas.every((l) => l.estSegur === 0)) {
    avisos.push(
      'Est.Segur. (dia atual) veio zerado. Importe de novo; se persistir, a coluna de dias no Excel pode ter outro nome.'
    );
  }

  return { linhas, avisos, nomeArquivo };
}

export function m2LinhaCalcPlanilha(
  linha: Pick<LinhaCalcPlanilha, 'produtoNome' | 'textoOriginal'>,
  pontuacoes: ReadonlyArray<{ nome: string; m2: number }>
): number {
  return m2Produto(linha.produtoNome, pontuacoes, linha.textoOriginal);
}
